import React from "react";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import CaseRow from "./CaseRow";
import { TnSkeleton } from "../components/TnSkeleton";

/*
Column config in the same { id, filter, label } shape the existing ticket table
uses, so it reads familiar.

`filter` is the DRF ordering field; a null means the column is not sortable.
The API exposes no ordering keys for retailer / sku / stage / sla_status, and
inventing field paths yields a silently-ignored param that looks like a broken
sort - so those headers stay plain.
*/
export const COLUMNS = [
  { id: "ticket_number", filter: "ticket_id", label: "Reference", width: "132px" },
  {
    id: "owner_name",
    filter: "user_device__customer__first_name",
    label: "Customer",
  },
  { id: "device_name", filter: "user_device__device__name", label: "Product" },
  { id: "retailer", filter: null, label: "Retailer" },
  { id: "type_label", filter: null, label: "Type", width: "96px" },
  { id: "stage", filter: null, label: "Stage", width: "140px" },
  { id: "sla_status", filter: null, label: "SLA", width: "150px" },
  {
    id: "last_activity_date",
    filter: "last_activity_date",
    label: "Updated",
    width: "96px",
  },
];

// `ordering` is a single string: "field" asc, "-field" desc.
const parseOrdering = function (ordering) {
  if (!ordering) return { field: null, direction: "asc" };
  if (ordering.charAt(0) === "-") {
    return { field: ordering.slice(1), direction: "desc" };
  }
  return { field: ordering, direction: "asc" };
};

const CaseTable = function (props) {
  const { rows, loading, hasLoadedOnce, error, ordering, onSort, onOpen, onRetry, onClearFilters } =
    props;

  const current = parseOrdering(ordering);
  const showSkeleton = loading && !hasLoadedOnce;

  const handleSort = function (column) {
    if (!column.filter) return;
    const isActive = current.field === column.filter;
    const nextDirection = isActive && current.direction === "asc" ? "desc" : "asc";
    onSort((nextDirection === "desc" ? "-" : "") + column.filter);
  };

  const renderBodyMessage = function (content) {
    return (
      <TableRow>
        <TableCell className="tn-cell tn-cell-message" colSpan={COLUMNS.length}>
          {content}
        </TableCell>
      </TableRow>
    );
  };

  let body;
  if (showSkeleton) {
    const skeletonRows = [];
    for (let i = 0; i < 8; i += 1) {
      skeletonRows.push(
        <TableRow key={i}>
          {COLUMNS.map(function (column) {
            return (
              <TableCell className="tn-cell" key={column.id}>
                <TnSkeleton h="13px" />
              </TableCell>
            );
          })}
        </TableRow>
      );
    }
    body = skeletonRows;
  } else if (error) {
    body = renderBodyMessage(
      <div className="tn-alert" role="alert">
        <span className="tn-alert-text">{error}</span>
        <button type="button" className="tn-alert-retry" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  } else if (!rows.length) {
    body = renderBodyMessage(
      <div className="tn-empty">
        No cases match these filters.
        <button type="button" className="tn-linkbtn" onClick={onClearFilters}>
          Clear filters
        </button>
      </div>
    );
  } else {
    body = rows.map(function (row, index) {
      return (
        <CaseRow key={row.id || row.ticket_number || index} row={row} onOpen={onOpen} />
      );
    });
  }

  return (
    <div className="tn-table-wrap">
      <Table className="tn-table" size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map(function (column) {
              const active = !!column.filter && current.field === column.filter;
              return (
                <TableCell
                  key={column.id}
                  className="tn-cell tn-head-cell"
                  style={column.width ? { width: column.width } : undefined}
                  sortDirection={active ? current.direction : false}
                >
                  {column.filter ? (
                    <TableSortLabel
                      active={active}
                      direction={active ? current.direction : "asc"}
                      onClick={function () {
                        handleSort(column);
                      }}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>{body}</TableBody>
      </Table>
    </div>
  );
};

export default CaseTable;
